/**
 * Formats a raw string value based on the Terraform variable type.
 * Useful for correcting user input (e.g. adding quotes to strings).
 */
export const formatTerraformValue = (value: string, type: string): string => {
  const cleanValue = value.trim();
  if (!type) return cleanValue;

  const t = type.toLowerCase();

  // 1. String
  if (t === 'string') {
    // Check if already quoted
    if (
      (cleanValue.startsWith('"') && cleanValue.endsWith('"')) ||
      (cleanValue.startsWith("'") && cleanValue.endsWith("'"))
    ) {
      return cleanValue;
    }
    // If empty, return empty quotes
    if (cleanValue === '') return '""';

    return `"${cleanValue}"`;
  }

  // 2. Number / Bool
  if (t === 'number' || t === 'bool' || t === 'boolean') {
    return cleanValue.replace(/["']/g, '').toLowerCase();
  }

  // 3. List(string) / Set(string)
  if (t.includes('list(string)') || t.includes('set(string)')) {
    if (cleanValue.startsWith('[') && cleanValue.endsWith(']')) return cleanValue;
    // Treat as CSV
    const items = cleanValue
      .split(',')
      .filter((i) => i.trim() !== '')
      .map((i) => {
        const item = i.trim();
        if (
          (item.startsWith('"') && item.endsWith('"')) ||
          (item.startsWith("'") && item.endsWith("'"))
        )
          return item;
        return `"${item}"`;
      });
    return `[${items.join(', ')}]`;
  }

  // 4. List(number) / Set(number)
  if (t.includes('list(number)') || t.includes('set(number)')) {
    if (cleanValue.startsWith('[') && cleanValue.endsWith(']')) return cleanValue;
    // Treat as CSV, strip quotes
    const items = cleanValue
      .split(',')
      .filter((i) => i.trim() !== '')
      .map((i) => i.trim().replace(/["']/g, ''));
    return `[${items.join(', ')}]`;
  }

  // 5. Generic List - Assume List of Strings if not bracketed, attempt primitive detection
  if (t.startsWith('list') || t.startsWith('set')) {
    if (cleanValue.startsWith('[') && cleanValue.endsWith(']')) return cleanValue;
    const items = cleanValue
      .split(',')
      .filter((i) => i.trim() !== '')
      .map((i) => {
        const item = i.trim();
        // Try to detect number or boolean, otherwise quote
        if (!isNaN(Number(item)) || item === 'true' || item === 'false') return item;
        if (
          (item.startsWith('"') && item.endsWith('"')) ||
          (item.startsWith("'") && item.endsWith("'"))
        )
          return item;
        return `"${item}"`;
      });
    return `[${items.join(', ')}]`;
  }

  return cleanValue;
};

/**
 * Recursively converts a JavaScript value to its HCL string representation.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertJsValueToHcl = (value: any): string => {
  if (value === null || value === undefined) return 'null';

  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string') {
    return `"${value}"`;
  }

  if (Array.isArray(value)) {
    const items = value.map((v) => convertJsValueToHcl(v)).join(', ');
    return `[${items}]`;
  }

  if (typeof value === 'object') {
    return convertJsObjectToHcl(value);
  }

  return String(value);
};

/**
 * Converts a JavaScript object to an HCL map/object syntax string.
 * e.g. { key = "value" } instead of JSON {"key":"value"}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertJsObjectToHcl = (obj: any): string => {
  if (!obj || typeof obj !== 'object') return '{}';

  // Recursively format entries
  const entries = Object.entries(obj).map(([key, val]) => {
    return `${key} = ${convertJsValueToHcl(val)}`;
  });

  return `{\n  ${entries.join('\n  ')}\n}`;
};
